/**
 * The small shared pieces — a status, a rule citation, a deadline, an amount.
 *
 * They are components rather than helpers because each one carries a decision that must be the
 * same everywhere: a deadline always shows a date *and* a countdown; an issue always names its
 * rule; a blocked thing never looks like a flagged thing.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Alert, Badge } from '@/components/ui';
import { formatCents } from '@/lib/money';
import { countdownTo, formatDate, formatInstant } from '@/lib/dates';
import { CLAIM_STATUS, PROPOSAL_STATUS, partitionIssues } from '@/lib/policy';
import type { ClaimStatus, Issue, ProposalStatus } from '@/lib/types';
import { cn } from '@/lib/utils';

export function ProposalStatusBadge({ status }: { status: ProposalStatus }) {
  const look = PROPOSAL_STATUS[status];
  return (
    <Badge tone={look.tone} title={look.hint}>
      {look.label}
    </Badge>
  );
}

export function ClaimStatusBadge({ status }: { status: ClaimStatus }) {
  const look = CLAIM_STATUS[status];
  return (
    <Badge tone={look.tone} title={look.hint}>
      {look.label}
    </Badge>
  );
}

/** Money, always formatted. `muted` for a figure that is context rather than the point. */
export function Money({
  cents,
  muted = false,
  className,
}: {
  cents: number | null | undefined;
  muted?: boolean;
  className?: string;
}) {
  if (cents === null || cents === undefined)
    return <span className="text-muted-foreground">—</span>;
  return (
    <span className={cn('tabular', muted ? 'text-muted-foreground' : '', className)}>
      {formatCents(cents)}
    </span>
  );
}

/**
 * A deadline as a date and a countdown.
 *
 * Both, always: "by 3 Sep" makes people count on their fingers, "in 6 days" hides what to put
 * in the calendar.
 */
export function Deadline({ date, today }: { date: string | null; today?: string }) {
  if (!date) return <span className="text-muted-foreground">—</span>;
  const countdown = today ? countdownTo(date, today) : countdownTo(date);
  return (
    <span className="whitespace-nowrap">
      {formatDate(date)}{' '}
      <span
        className={cn(
          'text-xs',
          countdown.overdue
            ? 'text-destructive font-medium'
            : countdown.urgent
              ? 'text-[var(--color-warning)]'
              : 'text-muted-foreground',
        )}
      >
        {countdown.label}
      </span>
    </span>
  );
}

/**
 * The issue list, partitioned.
 *
 * Errors block and read as walls. Warnings that need a justification read as questions. Plain
 * advisories read as notes. Collapsing the three into one red list is what would make a manager
 * of a 370-person department believe the app forbids an outing the policy permits.
 */
export function IssueList({ issues, className }: { issues: readonly Issue[]; className?: string }) {
  const { blocking, needJustification, advisory } = partitionIssues(issues);
  if (issues.length === 0) return null;

  return (
    <div className={cn('grid gap-2', className)}>
      {blocking.map((issue) => (
        <Alert key={issue.code} tone="bad" title={ruleTitle(issue, 'Cannot submit')}>
          {issue.message}
        </Alert>
      ))}
      {needJustification.map((issue) => (
        <Alert key={issue.code} tone="warn" title={ruleTitle(issue, 'Needs a reason')}>
          {issue.message}
        </Alert>
      ))}
      {advisory.map((issue) => (
        <Alert key={issue.code} tone="neutral" title={ruleTitle(issue, 'Note')}>
          {issue.message}
        </Alert>
      ))}
    </div>
  );
}

const ruleTitle = (issue: Issue, prefix: string): string =>
  issue.rule_id ? `${prefix} — ${issue.rule_id}` : prefix;

/**
 * "Last synced 4 hours ago", or a warning when the roster is stale.
 *
 * Shown wherever money is about to be committed: silent staleness on a confirmation screen is
 * worse than an outage, because the figures still look authoritative.
 */
export function RosterAge({
  freshness,
}: {
  freshness: { last_success_at: string | null; age_hours: number | null; stale: boolean };
}) {
  if (!freshness.last_success_at) {
    return (
      <Alert tone="warn" title="The roster has never been synced">
        Every figure on this screen is computed from an empty roster. Run the employee sync before
        trusting it.
      </Alert>
    );
  }
  const hours = Math.round(freshness.age_hours ?? 0);
  if (!freshness.stale) {
    return (
      <p className="text-muted-foreground text-xs">
        Roster synced {hours < 1 ? 'less than an hour' : `${hours} hours`} ago (
        {formatInstant(freshness.last_success_at)}).
      </p>
    );
  }
  return (
    <Alert tone="warn" title={`The roster is ${hours} hours old`}>
      The nightly sync has not run since {formatInstant(freshness.last_success_at)}. Someone who
      joined or left since then is not reflected in these figures.
    </Alert>
  );
}

export function RefLink({
  to,
  refNo,
  fallback = 'Draft',
}: {
  to: string;
  refNo: string | null;
  fallback?: string;
}) {
  return (
    <Link to={to} className="font-medium underline-offset-4 hover:underline">
      {refNo ?? fallback}
    </Link>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {subtitle ? <p className="text-muted-foreground mt-1 text-sm">{subtitle}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

/** An `ApiError`'s message, with its rule id when it has one — never "validation failed". */
export function ErrorNote({ error }: { error: unknown }) {
  if (!error) return null;
  const anyError = error as { message?: string; ruleId?: string; code?: string };
  return (
    <Alert tone="bad" title={anyError.ruleId ? `Refused — ${anyError.ruleId}` : 'Refused'}>
      {anyError.message ?? String(error)}
    </Alert>
  );
}
